import { useState, useEffect, useCallback } from 'react';
import { Search, Calendar, ChevronDown, ChevronUp, Users, Package, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PartySummary = ({ type }) => {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateFilter, setDateFilter] = useState('30');
  const [customRange, setCustomRange] = useState({ start: '', end: '' });
  const [expandedClient, setExpandedClient] = useState(null);

  const isTL = type === 'TL';
  const accentColor = isTL ? 'cyan' : 'indigo';
  const endpoint = isTL ? 'tl' : 'swatch';
  const unit = isTL ? 'm' : 'pcs';
  const codeField = isTL ? 'tl_code' : 'swatch_code';

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      let url = `${API}/${endpoint}/party-summary`;
      const params = new URLSearchParams();
      
      if (dateFilter === 'custom' && customRange.start && customRange.end) {
        params.append('start_date', customRange.start);
        params.append('end_date', customRange.end);
      } else if (dateFilter && dateFilter !== 'all') {
        params.append('days', dateFilter);
      }
      
      if (params.toString()) {
        url += `?${params}`;
      }
      
      const response = await axios.get(url);
      setSummary(response.data);
    } catch (error) {
      toast.error('Failed to load party summary');
    } finally {
      setLoading(false);
    }
  }, [endpoint, dateFilter, customRange]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  // Group by client name
  const groupedByClient = summary.reduce((acc, item) => {
    if (!acc[item.client_name]) {
      acc[item.client_name] = {
        client_name: item.client_name,
        total_quantity: 0,
        total_dispatches: 0,
        qualities: []
      };
    }
    acc[item.client_name].total_quantity += item.total_dispatched;
    acc[item.client_name].total_dispatches += item.dispatch_count;
    acc[item.client_name].qualities.push(item);
    return acc;
  }, {});

  const clientList = Object.values(groupedByClient).sort((a, b) => 
    b.total_quantity - a.total_quantity
  );

  const filteredClients = clientList.filter(client =>
    client.client_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate totals
  const totalDispatched = summary.reduce((sum, item) => sum + item.total_dispatched, 0);
  const totalClients = Object.keys(groupedByClient).length;

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-${accentColor}-100`}>
              <Users className={`w-5 h-5 text-${accentColor}-600`} />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Clients</p>
              <p className="text-2xl font-bold text-slate-900">{totalClients}</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-emerald-100`}>
              <Package className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Dispatched</p>
              <p className="text-2xl font-bold text-slate-900">
                {isTL ? totalDispatched.toFixed(2) : totalDispatched} <span className="text-sm font-normal text-slate-500">{unit}</span>
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg bg-amber-100`}>
              <TrendingUp className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-sm text-slate-500">Total Dispatches</p>
              <p className="text-2xl font-bold text-slate-900">{summary.reduce((sum, item) => sum + item.dispatch_count, 0)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search by client name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 h-10 rounded-lg border border-slate-200 text-sm focus:ring-2 focus:ring-cyan-500"
            data-testid="party-search-input"
          />
        </div>
        
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-slate-400" />
          <select
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
            className="h-10 px-3 rounded-lg border border-slate-200 text-sm"
            data-testid="party-date-filter"
          >
            <option value="7">Last 7 days</option>
            <option value="30">Last 30 days</option>
            <option value="90">Last 90 days</option>
            <option value="all">All time</option>
            <option value="custom">Custom range</option>
          </select>
        </div>

        {dateFilter === 'custom' && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customRange.start}
              onChange={(e) => setCustomRange({ ...customRange, start: e.target.value })}
              className="h-10 px-3 rounded-lg border border-slate-200 text-sm"
            />
            <span className="text-slate-400">to</span>
            <input
              type="date"
              value={customRange.end}
              onChange={(e) => setCustomRange({ ...customRange, end: e.target.value })}
              className="h-10 px-3 rounded-lg border border-slate-200 text-sm"
            />
          </div>
        )}
      </div>

      {/* Client List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="w-8 h-8 border-3 border-cyan-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-slate-500">Loading summary...</p>
        </div>
      ) : filteredClients.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl border border-slate-200">
          <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-500">No dispatches found</p>
          <p className="text-sm text-slate-400 mt-1">Try adjusting the date filter</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredClients.map((client) => (
            <div
              key={client.client_name}
              className="bg-white rounded-xl border border-slate-200 overflow-hidden"
              data-testid={`client-card-${client.client_name}`}
            >
              {/* Client Header */}
              <div
                className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                onClick={() => setExpandedClient(expandedClient === client.client_name ? null : client.client_name)}
              >
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-full bg-${accentColor}-100 flex items-center justify-center`}>
                    <span className={`text-${accentColor}-700 font-semibold`}>
                      {client.client_name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">{client.client_name}</h3>
                    <p className="text-sm text-slate-500">
                      {client.qualities.length} {client.qualities.length === 1 ? 'quality' : 'qualities'} • {client.total_dispatches} dispatches
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="text-right">
                    <p className="font-mono font-bold text-slate-900">
                      {isTL ? client.total_quantity.toFixed(2) : client.total_quantity} {unit}
                    </p>
                    <p className="text-xs text-slate-500">Total received</p>
                  </div>
                  {expandedClient === client.client_name ? (
                    <ChevronUp className="w-5 h-5 text-slate-400" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-slate-400" />
                  )}
                </div>
              </div>

              {/* Expanded Details */}
              {expandedClient === client.client_name && (
                <div className="border-t border-slate-200">
                  {client.qualities.map((quality, idx) => (
                    <div key={idx} className="border-b border-slate-100 last:border-0">
                      {/* Quality Header */}
                      <div className="px-4 py-3 bg-slate-50 flex items-center justify-between">
                        <div>
                          <span className={`text-xs font-mono px-2 py-0.5 rounded bg-${accentColor}-100 text-${accentColor}-700`}>
                            {quality[codeField]}
                          </span>
                          <span className="ml-2 font-medium text-slate-700">{quality.quality_name}</span>
                        </div>
                        <div className="font-mono text-slate-900">
                          {isTL ? quality.total_dispatched.toFixed(2) : quality.total_dispatched} {unit}
                        </div>
                      </div>
                      
                      {/* Dispatch History */}
                      <div className="px-4 py-2 max-h-48 overflow-y-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="text-slate-500">
                              <th className="text-left py-1 font-normal">Date</th>
                              <th className="text-right py-1 font-normal">Quantity</th>
                              {isTL && <th className="text-left py-1 font-normal pl-4">Color</th>}
                              <th className="text-left py-1 font-normal pl-4">Staff</th>
                            </tr>
                          </thead>
                          <tbody>
                            {quality.dispatches.map((dispatch, dIdx) => (
                              <tr key={dIdx} className="border-t border-slate-50">
                                <td className="py-1.5 text-slate-600">{formatDate(dispatch.date)}</td>
                                <td className="py-1.5 text-right font-mono">{dispatch.quantity} {unit}</td>
                                {isTL && <td className="py-1.5 pl-4 text-slate-600">{dispatch.color || '-'}</td>}
                                <td className="py-1.5 pl-4 text-slate-500">{dispatch.staff || '-'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default PartySummary;
